import {WASM} from './wasm.js';
import {Scene} from './scene.js';

import {Emitter, RetainEmitter} from './utils/event.js';
import {WonderlandEngine} from './engine.js';
import {InputType, ViewComponent} from './wonderland.js';

type WebXRFramebuffer = WebGLFramebuffer & {name: number};
type WebXRTexture = WebGLTexture & {name: number};

/**
 * Options for {@link LoadRuntimeOptions.xrOfferSession}
 *
 * @since 1.1.5
 */
export interface XROfferSessionOptions {
    /**
     * Mode to offer XR session with. If set to `'auto'`, offers a session
     * with the first supported mode in the following order:
     * - VR
     * - AR
     * - inline
     */
    mode: XRSessionMode | 'auto';
    /** Required features for the offered XR session */
    features: string[];
    /** Optional features for the offered XR session */
    optionalFeatures: string[];
}

/** Properties of a WebXR session */
export class XRSessionState {
    /** Wonderland WASM bridge. @hidden */
    readonly #webxr: WebXR;

    /** Current WebXR session mode */
    sessionMode: XRSessionMode;

    /** Current WebXR session */
    session: XRSession;

    /** Current WebXR frame */
    frame: XRFrame | null = null;

    /**
     * Constructor.
     *
     * @param wasm Wasm bridge instance
     * @param mode Current XR session mode
     *
     * @hidden
     */
    constructor(webxr: WebXR, mode: XRSessionMode, session: XRSession) {
        this.#webxr = webxr;
        this.sessionMode = mode;
        this.session = session;
    }

    /** @overload */
    referenceSpaceForType(type: 'viewer'): XRReferenceSpace;
    /**
     * Get a WebXR reference space of a given reference space type.
     *
     * @param type Type of reference space to get
     * @returns Reference space, or `null` if there's no reference space
     *     of the requested type available
     */
    referenceSpaceForType(type: XRReferenceSpaceType): XRReferenceSpace | null;
    referenceSpaceForType(type: XRReferenceSpaceType): XRReferenceSpace | null {
        return this.#webxr.referenceSpaceForType(type);
    }

    /** Set current reference space type used for retrieving eye, head, hand and joint poses */
    set currentReferenceSpace(refSpace: XRReferenceSpace) {
        this.#webxr.currentReferenceSpace = refSpace;
    }

    /** Current reference space type used for retrieving eye, head, hand and joint poses */
    get currentReferenceSpace(): XRReferenceSpace {
        return this.#webxr.currentReferenceSpace;
    }

    /** Current WebXR reference space type or `null` if not a default reference space */
    get currentReferenceSpaceType(): XRReferenceSpaceType {
        return this.#webxr.currentReferenceSpaceType;
    }

    /** Current WebXR base layer  */
    get baseLayer(): XRProjectionLayer | XRWebGLLayer {
        return this.#webxr.baseLayer!;
    }

    /** Current WebXR framebuffer */
    get framebuffers(): WebGLFramebuffer[] {
        return this.#webxr.framebuffers;
    }
}

/**
 * WebXR API (internal)
 *
 * @hidden
 */
export class WebXR {
    /** Wonderland WASM bridge. @hidden */
    readonly #wasm: WASM;

    readonly engine: WonderlandEngine;

    sessionState: XRSessionState | null = null;

    colorFormat: number = 0x8058 /* RGBA8 */;
    depthFormat: number = 0x88f0 /* DEPTH24_STENCIL8 */;
    textureType: XRTextureType = 'texture';

    /**
     * {@link Emitter} for WebXR session end events.
     */
    readonly onSessionEnd = new Emitter();

    /**
     * {@link RetainEmitter} for WebXR session start events.
     */
    readonly onSessionStart: Emitter<[XRSession, XRSessionMode]> = new RetainEmitter<
        [XRSession, XRSessionMode]
    >();

    /**
     * {@link Emitter} notified before the first update and rendering on the first frame of a new XR session.
     */
    readonly onSessionFirstFrame: Emitter<[XRSession, XRSessionMode]> = new Emitter<
        [XRSession, XRSessionMode]
    >();

    /** Whether AR is supported by the browser. */
    arSupported: boolean = false;

    /** Whether VR is supported by the browser. */
    vrSupported: boolean = false;

    /**
     * Current WebXR base layer or `null` if no session active.
     */
    baseLayer: XRProjectionLayer | XRWebGLLayer | null = null;

    /** WebXR framebuffer scale factor. */
    framebufferScaleFactor: number = 1.0;

    private _webglBinding: XRWebGLBinding | null = null;

    private _initXR: boolean = false;
    private _inXR: boolean = false;

    /** WebXR reference spaces. */
    private _refSpaces: Record<XRReferenceSpaceType, XRReferenceSpace | undefined> = {
        'viewer': undefined,
        'local': undefined,
        'local-floor': undefined,
        'bounded-floor': undefined,
        'unbounded': undefined,
    };

    /** Current WebXR reference space. */
    private _refSpace: XRReferenceSpace | null = null;

    /** Current WebXR reference space type. */
    private _refSpaceType: XRReferenceSpaceType | null = null;

    /**
     * Emscripten WebXR framebuffer(s).
     *
     * @note fbo will not get overwritten if we are rendering to the
     * default framebuffer, e.g., when using WebXR emulator.
     */
    private _fbo: number | number[] = 0;

    private _allowLayers: boolean = false;
    private _requestAnimationFrameId: any = null;

    /**
     * Initial WebXR reference space type. See {@link init} for
     * more information.
     */
    private _initialReferenceSpaceType: XRReferenceSpaceType | null = null;

    private _tempPosition: Float32Array = new Float32Array(3);
    private _tempRotation: Float32Array = new Float32Array(4);

    private _tempPose(transform: XRRigidTransform) {
        const p = transform.position;
        this._tempPosition[0] = p.x;
        this._tempPosition[1] = p.y;
        this._tempPosition[2] = p.z;
        const o = transform.orientation;
        this._tempRotation[0] = o.x;
        this._tempRotation[1] = o.y;
        this._tempRotation[2] = o.z;
        this._tempRotation[3] = o.w;
    }

    /**
     * Constructor.
     *
     * @param engine Engine
     *
     * @hidden
     */
    constructor(engine: WonderlandEngine) {
        this.engine = engine;
        this.#wasm = engine.wasm;
    }

    /**
     * Check whether XR is supported and store the result in {@link arSupported} and {@link vrSupported}
     */
    checkXRSupport(): Promise<boolean> {
        if (typeof navigator === 'undefined') {
            return Promise.resolve(false);
        }
        if (!navigator.xr) {
            const isLocalhost =
                location.hostname === 'localhost' || location.hostname === '127.0.0.1';
            const missingHTTPS = location.protocol !== 'https:' && !isLocalhost;
            console.warn(
                missingHTTPS
                    ? 'WebXR is only supported with HTTPS or on localhost!'
                    : 'WebXR unsupported in this browser.'
            );
            return Promise.resolve(false);
        }
        /* Those can throw if the application is embedded in an iframe without
         * xr-spatial-tracking permission. navigator.xr will be available, but
         * any access of it will throw. */
        const vrPromise = navigator.xr
            .isSessionSupported('immersive-vr')
            .then((supported) => (this.vrSupported = supported))
            .catch(() => (this.vrSupported = false));
        const arPromise = navigator.xr
            .isSessionSupported('immersive-ar')
            .then((supported) => (this.arSupported = supported))
            .catch(() => (this.arSupported = false));

        return Promise.all([vrPromise, arPromise]).then(
            () => this.vrSupported || this.arSupported
        );
    }

    /**
     * Initialize WebXR.
     *
     * @hidden
     */
    async init(
        framebufferScaleFactor: number,
        offerSessionOptions: XROfferSessionOptions | null
    ) {
        const supported = await this.checkXRSupport();
        if (!supported) return;

        this.framebufferScaleFactor = framebufferScaleFactor;

        /* Force the reference space to 'local'/'viewer' for the loading screen
         * to make sure the head input is at the origin. Doing it this way to
         * avoid adding JS components to the loading screen. */
        const onXRStart = () => {
            this._initialReferenceSpaceType = this.currentReferenceSpaceType;
            const newSpace = this._refSpaces['local'] ?? this._refSpaces['viewer']!;
            this.currentReferenceSpace = newSpace;
        };

        /* Not once() because the user can enter and exit XR several times
         * during a long loading screen */
        this.onSessionStart.add(onXRStart);

        /* This is called before all init()/start()/onActivate() so we avoid
         * overwriting a user-set reference space */
        this.engine.onLoadingScreenEnd.once(() => {
            this.onSessionStart.remove(onXRStart);

            if (!this._initialReferenceSpaceType) return;

            this.currentReferenceSpace =
                this._refSpaces[this._initialReferenceSpaceType] ??
                this._refSpaces['viewer']!;
            this._initialReferenceSpaceType = null;
        });

        if (offerSessionOptions !== null) {
            let mode = offerSessionOptions.mode;
            if (mode == 'auto') {
                if (this.vrSupported) {
                    mode = 'immersive-vr';
                } else if (this.arSupported) {
                    mode = 'immersive-ar';
                } else {
                    mode = 'inline';
                }
            }

            const offerSession = () => {
                this.offerSession(
                    mode as XRSessionMode,
                    offerSessionOptions.features,
                    offerSessionOptions.optionalFeatures
                )
                    .then(
                        /* When the session ends, offer to start a new one again */
                        (s) => s.addEventListener('end', offerSession)
                    )
                    /* The browser may not suppoer offer session, or a previous request
                     * was replaced by a new one. Not crucial, but inform the user. */
                    .catch(console.warn);
            };

            offerSession();
        }
    }

    /** Get a WebXR reference space of a given reference space type. */
    referenceSpaceForType(type: XRReferenceSpaceType): XRReferenceSpace | null {
        return this._refSpaces[type] ?? null;
    }

    /** Set current reference space type used for retrieving eye, head, hand and joint poses */
    set currentReferenceSpace(refSpace: XRReferenceSpace) {
        this._refSpace! = refSpace;

        this._refSpaceType = null;
        for (const type of Object.keys(this._refSpaces) as XRReferenceSpaceType[]) {
            if (this._refSpaces[type] === refSpace) {
                /* Keep track of reference space type */
                this._refSpaceType = type;
            }
        }
    }

    /** Current reference space type used for retrieving eye, head, hand and joint poses */
    get currentReferenceSpace(): XRReferenceSpace {
        return this._refSpace!;
    }

    /** Current WebXR reference space type or `null` if not a default reference space */
    get currentReferenceSpaceType(): XRReferenceSpaceType {
        return this._refSpaceType!;
    }

    /** Current WebXR framebuffer */
    get framebuffers(): WebGLFramebuffer[] {
        if (!Array.isArray(this._fbo)) {
            return [this.#wasm.GL.framebuffers[this._fbo]];
        }
        return this._fbo.map((id) => this.#wasm.GL.framebuffers[id]);
    }

    /**
     * Set main/left/right eye view active according to XR mode.
     *
     * If @param inXR is `true`, disables the main view and enables the left and
     * right view. Otherwise, enables the main view and disables the left and
     * right view.
     *
     * @param inXR Whether an XR session is currently active
     */
    updateViewState(inXR: boolean | undefined) {
        const scene: Scene = this.engine.scene;

        inXR = inXR ?? this._inXR;

        if (scene.mainView) {
            scene.mainView.active = !inXR;
        }

        if (scene.leftEyeView && scene.rightEyeView) {
            /** We always have either no left/right eye views or both. Always enable
             * both since we can't know if this is single-view AR until we get the
             * view count in {@link nextFrame}. There the right eye view is
             * disabled if necessary. */
            scene.leftEyeView.active = inXR;
            scene.rightEyeView.active = inXR;
        }
    }

    /**
     * Set projection matrix parameters for the webxr session.
     *
     * @param near Distance of near clipping plane
     * @param far Distance of far clipping plane
     */
    updateProjectionParams(near: number | undefined, far: number | undefined) {
        if (!this.sessionState) return;

        const scene = this.engine.scene;
        near ??= scene.leftEyeView!.near;
        far ??= scene.leftEyeView!.far;

        /* If reverse-Z is enabled, we need to give WebXR swapped near and far
         * planes so it generates the correct projection matrices. This is
         * explicitly supported by the spec. */
        const reverseZ = !!this.engine.isReverseZEnabled;

        this.sessionState!.session.updateRenderState({
            depthNear: reverseZ ? far : near,
            depthFar: reverseZ ? near : far,
        });
    }

    /**
     * Request an XR session.
     *
     * @note Please use this call instead of directly calling `navigator.xr.requestSession()`.
     * Wonderland Engine requires to be aware that a session is started, and this
     * is done through this call.
     *
     * @param mode The XR mode.
     * @param features An array of required features, e.g., `['local-floor', 'hit-test']`.
     * @param optionalFeatures An array of optional features, e.g., `['bounded-floor', 'depth-sensing']`.
     * @returns A promise resolving with the `XRSession`, a string error message otherwise.
     */
    requestSession(
        mode: XRSessionMode,
        features: string[],
        optionalFeatures: string[] = []
    ): Promise<XRSession> {
        const options = this.sessionOptions(features, optionalFeatures);
        return navigator.xr!.requestSession(mode, options).then(async (s) => {
            /* Ensure our context can handle WebXR rendering */
            const gl = this.engine.canvas.getContext('webgl2')!;
            await gl.makeXRCompatible();
            this.startSession(s, mode);
            return s;
        });
    }

    /**
     * Offer an XR session.
     *
     * Adds an interactive UI element to the browser interface to start an XR
     * session. Browser support is optional, so it's advised to still allow
     * requesting a session with a UI element on the website itself.
     *
     * @note Please use this call instead of directly calling `navigator.xr.offerSession()`.
     * Wonderland Engine requires to be aware that a session is started, and this
     * is done through this call.
     *
     * @param mode The XR mode.
     * @param features An array of required features, e.g., `['local-floor', 'hit-test']`.
     * @param optionalFeatures An array of optional features, e.g., `['bounded-floor', 'depth-sensing']`.
     * @returns A promise resolving with the `XRSession`, a string error message otherwise.
     *
     * @since 1.1.5
     */
    offerSession(
        mode: XRSessionMode,
        features: string[],
        optionalFeatures: string[] = []
    ): Promise<XRSession> {
        /* @ts-ignore */
        if (!navigator.xr!.offerSession) {
            return Promise.reject('WebXR offerSession() unsupported in this browser.');
        }
        const options = this.sessionOptions(features, optionalFeatures);
        /* @ts-ignore */
        return navigator.xr!.offerSession(mode, options).then(async (s) => {
            /* Ensure our context can handle WebXR rendering */
            const gl = this.engine.canvas.getContext('webgl2')!;
            await gl.makeXRCompatible();
            this.startSession(s, mode);
            return s;
        });
    }

    async startSession(session: XRSession, mode: XRSessionMode) {
        /* Multiple calls to requestSession will result in the existing
         * session being passed to the callback */
        if (session == this.sessionState?.session) return;
        this.sessionState = new XRSessionState(this, mode, session);

        /* React to session ending */
        session.addEventListener('end', () => {
            this.endSession();
        });

        const gl = this.engine.canvas.getContext('webgl2')!;

        /* Create the base layer */
        let binding = null;
        const isEmulator = 'CustomWebXRPolyfill' in window;
        /* The WebXR emulator doesn't support layers, and will
         * simply throw an exception. Only way of checking seems
         * to be whether the custom polyfill is available */
        if (!isEmulator && 'XRWebGLBinding' in window) {
            binding = this._webglBinding = new XRWebGLBinding(session, gl);
        }
        const useLayers = this._allowLayers && binding;
        if (useLayers) {
            /* Use WebXR Layers */
            const layer = (this.baseLayer = binding!.createProjectionLayer({
                scaleFactor: this.framebufferScaleFactor,
                colorFormat: this.colorFormat,
                depthFormat: this.depthFormat,
                /* Always use 'texture-array' with multiview, otherwise default to it
                 * if not set otherwise */
                textureType: !this.textureType ? 'texture-array' : this.textureType,
            }));
            session.updateRenderState({layers: [layer]});
        } else {
            const layer = (this.baseLayer = new XRWebGLLayer(session, gl, {
                framebufferScaleFactor: this.framebufferScaleFactor,
            }));
            session.updateRenderState({baseLayer: layer});
        }

        /* Request and cache available spaces. 'viewer' is always available. */
        const promises = [];
        for (const type of Object.keys(this._refSpaces) as XRReferenceSpaceType[]) {
            promises.push(
                session.requestReferenceSpace(type).then((refSpace) => {
                    this._refSpaces[type] = refSpace;
                    return refSpace;
                })
            );
        }

        await Promise.allSettled(promises).then((results) => {
            /* We prefer the reference space automatically in above order */
            for (let i = results.length - 1; i >= 0; --i) {
                const result = results[i];
                if (result.status === 'rejected') continue;
                /* Type is kept track of for the user to test against */
                this._refSpaceType = Object.keys(this._refSpaces)[
                    i
                ] as XRReferenceSpaceType;
                this._refSpace = result.value;
                break;
            }
        });

        const isVR = mode == 'immersive-vr';

        const scene: Scene = this.engine.scene;

        if (isVR && (!scene.leftEyeView || !scene.rightEyeView)) {
            console.error(
                `sessionStart(): VR sessions require 1 classic view, a left and a right view`
            );
            return;
        }

        this.updateViewState(true);

        this.updateProjectionParams(undefined, undefined);

        this.onSessionStart.notify(session, this.sessionState!.sessionMode);

        this.#wasm._wl_reset_context();

        console.log(`WebXR ${isVR ? 'VR' : 'AR'} session started`);

        this.sessionState.session.addEventListener('visibilitychange', (event) => {
            switch (event.session.visibilityState) {
                case 'visible':
                    this.#wasm._wl_xr_focus();
                case 'visible-blurred':
                    this.#wasm._wl_xr_blur();
                case 'hidden':
                    this.#wasm._wl_xr_hide();
            }
        });

        /* Wait until first frame to call _wl_xr_init() */
        this._initXR = true;

        this._requestAnimationFrameId = session.requestAnimationFrame(
            useLayers ? this.nextFrameLayers.bind(this) : this.nextFrameSingle.bind(this)
        );
    }

    sessionOptions(
        requiredFeatures: string[],
        optionalFeatures: string[] = []
    ): XRSessionInit {
        if (this._allowLayers && !optionalFeatures.includes('layers')) {
            optionalFeatures.push('layers');
        }

        const params: XRSessionInit = {
            requiredFeatures,
            optionalFeatures,
        };

        const hasDepthSensing =
            requiredFeatures.includes('depth-sensing') ||
            optionalFeatures.includes('depth-sensing');
        if (hasDepthSensing) {
            params.depthSensing = {
                usagePreference: ['gpu-optimized', 'cpu-optimized'],
                dataFormatPreference: ['float32', 'luminance-alpha'],
            };
        }
        return params;
    }

    endSession() {
        this.sessionState!.session.cancelAnimationFrame(this._requestAnimationFrameId);
        this._requestAnimationFrameId = null;
        this.sessionState = null;

        this.#wasm._wl_reset_context();

        if (this.onSessionStart instanceof RetainEmitter) this.onSessionStart.reset();
        this.onSessionEnd.notify();

        console.log('WebXR session ended');

        this._inXR = false;

        this.updateViewState(false);

        const scene: Scene = this.engine.scene;

        this.#wasm._wl_xr_exit();

        /* Restore projection matrices. They are temporarily overridden in XR and
         * view changes are applied when the session ends. */
        scene.leftEyeView!._generateProjectionMatrix();
        scene.rightEyeView!._generateProjectionMatrix();

        /* Meta WebXR emulator doesn't request animation frame. Quest does. */
        this.#wasm._wl_application_redraw();
    }

    nextFrame(time: number, frame: XRFrame) {
        this.sessionState!.frame = frame;

        const session = frame.session;

        const pose = frame.getViewerPose(this._refSpace!);
        if (!pose) return;

        const scene: Scene = this.engine.scene;

        if (this._initXR) {
            /* There's no way to tell how many views we have before rendering the first frame,
             * and AR is not a sufficient criteria for one eye (mobile AR, AR headsets) */
            scene.rightEyeView!.active = pose.views.length > 1;

            this.#wasm._wl_xr_init(pose.views.length, !this.baseLayer);
            this._initXR = false;
            this._inXR = true;

            this.onSessionFirstFrame.notify(session, this.sessionState!.sessionMode);
        }

        /* Head transformation */
        this._tempPose(pose.transform);
        scene._setInputTransformation(
            InputType.Head,
            this._tempPosition,
            this._tempRotation
        );

        /* Eyes transformations */
        for (let i = 0; i < pose.views.length && i < 2; ++i) {
            const view = pose.views[i];
            const viewIndex = view.eye == 'right' ? 1 : 0;
            this._tempPose(view.transform);
            scene._setInputTransformation(
                InputType.EyeLeft + viewIndex,
                this._tempPosition,
                this._tempRotation
            );
        }

        /* Controller transformations */
        for (const inputSource of session.inputSources) {
            let handedness = -1;
            if (inputSource.handedness == 'left') handedness = 0;
            else if (inputSource.handedness == 'right') handedness = 1;
            this._updateInputComponent(
                frame,
                inputSource.gripSpace,
                InputType.ControllerLeft + handedness
            );
            this._updateInputComponent(
                frame,
                inputSource.targetRaySpace,
                InputType.RayLeft + handedness
            );
        }

        this.#wasm._wl_nextFrame(0);
    }

    nextFrameSingle(time: number, frame: XRFrame) {
        const session = frame.session;
        /* Request next frame */
        if (this.sessionState != null) {
            this._requestAnimationFrameId = session.requestAnimationFrame(
                this.nextFrameSingle.bind(this)
            );
        }

        const pose = frame.getViewerPose(this._refSpace!);
        if (!pose) return;

        const gl = this.engine.canvas.getContext('webgl2')!;
        const GL = this.#wasm.GL as any;

        /* If framebuffer is non-null, compositor is enabled and we bind it.
         * If it's null, we need to avoid this call otherwise the canvas FBO is bound */
        const glLayer = session.renderState.baseLayer!;
        if (glLayer.framebuffer) {
            /* Make sure that FRAMEBUFFER_BINDING returns a valid value.
             * For that we create an id in the emscripten object tables
             * and add the framebuffer */
            const id = this._fbo || GL.getNewId(GL.framebuffers);
            (glLayer.framebuffer as WebXRFramebuffer).name = id;
            GL.framebuffers[id] = glLayer.framebuffer;
            this._fbo = id;
            gl.bindFramebuffer(gl.FRAMEBUFFER, GL.framebuffers[id]);
        }

        for (let i = 0; i < pose.views.length; ++i) {
            const view = pose.views[i];
            const viewport = glLayer.getViewport(view)!;

            let destView: ViewComponent;
            if (view.eye == 'left') {
                destView = this.engine.scene.leftEyeView!;
            } else if (view.eye == 'right') {
                destView = this.engine.scene.rightEyeView!;
            } else {
                destView = this.engine.scene._components.componentAt(ViewComponent, i);
            }
            destView._setViewport(viewport.x, viewport.y, viewport.width, viewport.height);
            destView._setProjectionMatrix(view.projectionMatrix);
            /* Patch projection matrix, currently only needed for reverse-Z.
             * If reverse-Z is enabled, we pass swapped near and far values to
             * updateRenderState(), giving us a matrix with inverted depth. But
             * we still need to map from NDC 1,-1 to 1,0. On WebGPU this won't
             * be necessary. */
            const ndcDepthIsZeroToOne = false;
            this.#wasm._wl_view_component_remapProjectionMatrix(
                destView._id,
                this.engine.isReverseZEnabled,
                ndcDepthIsZeroToOne
            );
            this.#wasm._wl_view_component_set_externalFramebuffer(
                destView._id,
                this._fbo as number
            );
        }

        this.nextFrame(time, frame);
    }

    nextFrameLayers(time: number, frame: XRFrame) {
        const session = frame.session;
        /* Request next frame */
        if (this.sessionState != null) {
            this._requestAnimationFrameId = session.requestAnimationFrame(
                this.nextFrameLayers.bind(this)
            );
        }

        const scene: Scene = this.engine.scene;

        const pose = frame.getViewerPose(this._refSpace!)!;

        const layer = <XRProjectionLayer>this.baseLayer!;
        const binding = this._webglBinding;

        /* Make sure that FRAMEBUFFER_BINDING returns a valid value.
         * For that we create an id in the emscripten object tables
         * and add the framebuffer */
        const gl = this.engine.canvas.getContext('webgl2')!;
        const GL = this.#wasm.GL as any;

        const createFramebuffer = !this._fbo;
        const textureArray = layer.textureArrayLength == 2;

        if (createFramebuffer) {
            if (textureArray) {
                this._fbo = [GL.getNewId(GL.framebuffers), GL.getNewId(GL.framebuffers)];
            } else {
                /* Both eyes use the same FBO */
                const id = GL.getNewId(GL.framebuffers);
                this._fbo = [id, id];
            }
            this._fbo.forEach((id) => {
                const framebuffer = gl.createFramebuffer() as WebXRFramebuffer;
                GL.framebuffers[id] = framebuffer;
                framebuffer.name = id;
            });
        }

        const ids = this._fbo as number[];
        pose.views.forEach((view) => {
            const viewIndex = view.eye == 'right' ? 1 : 0;

            const subImage = binding!.getViewSubImage(layer, view);
            const viewport = subImage.viewport;

            let destView: ViewComponent;
            if (view.eye == 'right') {
                destView = this.engine.scene.rightEyeView!;
            } else {
                destView = this.engine.scene.leftEyeView!;
            }
            destView._setViewport(viewport.x, viewport.y, viewport.width, viewport.height);
            destView._setProjectionMatrix(view.projectionMatrix);
            const ndcDepthIsZeroToOne = false;
            this.#wasm._wl_view_component_remapProjectionMatrix(
                destView._id,
                this.engine.isReverseZEnabled,
                ndcDepthIsZeroToOne
            );
            this.#wasm._wl_view_component_set_externalFramebuffer(
                destView._id,
                ids[viewIndex]
            );

            if (!createFramebuffer) return;
            /* Only needs to be done for the first view on when using side-by-side
             * target texture */
            if (!textureArray && viewIndex != 0) return;

            /* Make WebXR API created textures known to emscripten */
            const colorTexture = subImage.colorTexture as WebXRTexture;
            const colorImageId = (colorTexture.name =
                colorTexture.name || GL.getNewId(GL.textures));
            GL.textures[colorImageId] = colorTexture;

            const depthStencilTexture = subImage.depthStencilTexture as WebXRTexture;
            const depthStencilImageId = (depthStencilTexture.name =
                depthStencilTexture.name || GL.getNewId(GL.textures));
            GL.textures[depthStencilImageId] = depthStencilTexture;

            /* Attach textures to framebuffer */
            gl.bindFramebuffer(gl.FRAMEBUFFER, GL.framebuffers[ids[viewIndex]]);

            if (textureArray) {
                /* @ts-ignore */
                gl.framebufferTextureLayer(
                    gl.FRAMEBUFFER,
                    gl.COLOR_ATTACHMENT0,
                    subImage.colorTexture,
                    0,
                    subImage.imageIndex
                );
                /* @ts-ignore */
                gl.framebufferTextureLayer(
                    gl.FRAMEBUFFER,
                    gl.DEPTH_STENCIL_ATTACHMENT,
                    subImage.depthStencilTexture,
                    0,
                    subImage.imageIndex
                );
            } else {
                gl.framebufferTexture2D(
                    gl.FRAMEBUFFER,
                    gl.COLOR_ATTACHMENT0,
                    gl.TEXTURE_2D,
                    subImage.colorTexture,
                    0
                );
                gl.framebufferTexture2D(
                    gl.FRAMEBUFFER,
                    gl.DEPTH_STENCIL_ATTACHMENT,
                    gl.TEXTURE_2D,
                    subImage.depthStencilTexture,
                    0
                );
            }

            if (!gl.checkFramebufferStatus(gl.FRAMEBUFFER)) {
                console.error('Target framebuffer for', view.eye, 'eye is incomplete.');
            }
        });

        this.nextFrame(time, frame);
    }

    private _updateInputComponent(
        frame: XRFrame,
        space: XRSpace | undefined,
        inputType: InputType
    ) {
        if (!space) return;
        const pose = frame.getPose(space, this._refSpace!);
        if (!pose || Number.isNaN(pose.transform.matrix[0])) return;
        this._tempPose(pose.transform);
        this.engine.scene._setInputTransformation(
            inputType,
            this._tempPosition,
            this._tempRotation
        );
    }
}
